export type EventType = string | symbol;

export type Handler<T = unknown> = (event: T) => void;
export type WildcardHandler<T = Record<string, unknown>> = (type: keyof T, event: T[keyof T]) => void;

export type EventHandlerList<T = unknown> = Array<Handler<T>>;
export type WildCardEventHandlerList<T = Record<string, unknown>> = Array<WildcardHandler<T>>;

export type EventHandlerMap<Events extends Record<EventType, unknown>> = Map<
	keyof Events | "*",
	EventHandlerList<Events[keyof Events]> | WildCardEventHandlerList<Events>
>;

export interface Emitter<Events extends Record<EventType, unknown>> {
	all: EventHandlerMap<Events>;
	on<Key extends keyof Events>(type: Key, handler: Handler<Events[Key]>): () => void;
	on(type: "*", handler: WildcardHandler<Events>): () => void;
	once<Key extends keyof Events>(type: Key, handler: Handler<Events[Key]>): () => void;
	once(type: "*", handler: WildcardHandler<Events>): () => void;
	waitFor<Key extends keyof Events>(type: Key): Promise<Events[Key]>;
	waitFor(type: "*"): Promise<Events[any]>;
	off<Key extends keyof Events>(type: Key, handler?: Handler<Events[Key]>): void;
	off(type: "*", handler: WildcardHandler<Events>): void;
	emit<Key extends keyof Events>(type: Key, event: Events[Key]): void;
	emit<Key extends keyof Events>(type: undefined extends Events[Key] ? Key : never): void;
}

/** Internal typed event emitter for inbound bridge listeners. Not part of the public API. */
export function createEmitter<Events extends Record<EventType, unknown>>(all: EventHandlerMap<Events> = new Map()): Emitter<Events> {
	type GenericEventHandler = Handler<Events[keyof Events]> | WildcardHandler<Events>;

	const instance = {
		all,
		on<Key extends keyof Events>(type: Key, handler: GenericEventHandler) {
			const handlers: Array<GenericEventHandler> | undefined = all.get(type);
			if (handlers) {
				handlers.push(handler);
			} else {
				all.set(type, [handler] as EventHandlerList<Events[keyof Events]>);
			}
			return () => instance.off(type, handler);
		},
		once<Key extends keyof Events>(type: Key, handler: GenericEventHandler) {
			const onceHandler: GenericEventHandler = (arg0, arg1) => {
				instance.off(type, onceHandler);
				return handler(arg0 as keyof Events & Events[keyof Events], arg1);
			};
			return instance.on(type, onceHandler);
		},
		waitFor<Key extends keyof Events>(type: Key) {
			return new Promise<Events[Key]>((resolve) => {
				instance.once(type, (e: any) => resolve(e));
			});
		},
		off<Key extends keyof Events>(type: Key, handler?: GenericEventHandler) {
			const handlers: Array<GenericEventHandler> | undefined = all.get(type);
			if (handlers) {
				if (handler) {
					handlers.splice(handlers.indexOf(handler) >>> 0, 1);
				} else {
					all.delete(type);
				}
			}
		},
		emit<Key extends keyof Events>(type: Key, evt?: Events[Key]) {
			let handlers = all.get(type);
			if (handlers) {
				(handlers as EventHandlerList<Events[keyof Events]>).slice().map(async (handler) => {
					try {
						return await handler(evt as Events[keyof Events]);
					} catch (error) {
						console.error(error);
						return null;
					}
				});
			}

			handlers = all.get("*");
			if (handlers) {
				(handlers as WildCardEventHandlerList<Events>).slice().map(async (handler) => {
					try {
						return await handler(type, evt as Events[keyof Events]);
					} catch (error) {
						console.error(error);
						return null;
					}
				});
			}
		},
	};

	return instance;
}
